import Link from "next/link";
import { humanize } from "@/lib/utils";
import type { ComponentProps, ReactNode } from "react";

export function Card({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-xl border border-slate-200 bg-white shadow-sm ${className}`}
    >
      {children}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
}) {
  return (
    <Card className="p-5">
      <div className="text-sm font-medium text-slate-500">{label}</div>
      <div className="mt-2 text-3xl font-semibold text-slate-900">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
    </Card>
  );
}

type ButtonProps = ComponentProps<"button"> & {
  variant?: "primary" | "secondary" | "danger" | "ghost";
};

const buttonStyles: Record<NonNullable<ButtonProps["variant"]>, string> = {
  primary: "bg-brand-600 text-white hover:bg-brand-700",
  secondary: "bg-white text-slate-700 border border-slate-300 hover:bg-slate-50",
  danger: "bg-red-600 text-white hover:bg-red-700",
  ghost: "text-slate-600 hover:bg-slate-100",
};

export function Button({
  variant = "primary",
  className = "",
  ...props
}: ButtonProps) {
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${buttonStyles[variant]} ${className}`}
      {...props}
    />
  );
}

export function LinkButton({
  href,
  variant = "primary",
  className = "",
  children,
}: {
  href: string;
  variant?: NonNullable<ButtonProps["variant"]>;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Link
      href={href}
      className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition ${buttonStyles[variant]} ${className}`}
    >
      {children}
    </Link>
  );
}

// Status colour mapping — covers every lifecycle enum used in the app.
const STATUS_TONES: Record<string, string> = {
  // positive / done
  APPROVED: "bg-green-100 text-green-700",
  PAID: "bg-green-100 text-green-700",
  AVAILABLE: "bg-green-100 text-green-700",
  COMPLETED: "bg-green-100 text-green-700",
  ADDED_TO_INVENTORY: "bg-green-100 text-green-700",
  RETURNED: "bg-green-100 text-green-700",
  ACTIVE: "bg-green-100 text-green-700",
  ACTIVE_RENTAL: "bg-green-100 text-green-700",
  // in progress
  SUBMITTED: "bg-blue-100 text-blue-700",
  UNDER_REVIEW: "bg-blue-100 text-blue-700",
  PENDING_REVIEW: "bg-blue-100 text-blue-700",
  IN_PROCUREMENT: "bg-blue-100 text-blue-700",
  PREPARING: "bg-blue-100 text-blue-700",
  READY_FOR_PICKUP: "bg-indigo-100 text-indigo-700",
  PICKED_UP: "bg-indigo-100 text-indigo-700",
  QUOTATION_SENT: "bg-indigo-100 text-indigo-700",
  RESERVED: "bg-amber-100 text-amber-700",
  RENTED: "bg-amber-100 text-amber-700",
  // waiting
  DRAFT: "bg-slate-100 text-slate-600",
  NEW_REQUEST: "bg-slate-100 text-slate-600",
  REQUESTED: "bg-slate-100 text-slate-600",
  PENDING: "bg-amber-100 text-amber-700",
  PAYMENT_PENDING: "bg-amber-100 text-amber-700",
  EXPIRING: "bg-amber-100 text-amber-700",
  PARTIALLY_PAID: "bg-amber-100 text-amber-700",
  UNDER_MAINTENANCE: "bg-amber-100 text-amber-700",
  // negative
  REJECTED: "bg-red-100 text-red-700",
  CANCELLED: "bg-red-100 text-red-700",
  DAMAGED: "bg-red-100 text-red-700",
  LOST: "bg-red-100 text-red-700",
  FAILED: "bg-red-100 text-red-700",
  OVERDUE: "bg-red-100 text-red-700",
  RETIRED: "bg-slate-200 text-slate-500",
  CLOSED: "bg-slate-200 text-slate-600",
  ENDED: "bg-slate-200 text-slate-600",
};

export function StatusBadge({ value }: { value: string }) {
  const tone = STATUS_TONES[value] ?? "bg-slate-100 text-slate-600";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${tone}`}
    >
      {humanize(value)}
    </span>
  );
}

export function Badge({
  children,
  tone = "slate",
}: {
  children: ReactNode;
  tone?: "slate" | "green" | "red" | "blue" | "amber";
}) {
  const tones = {
    slate: "bg-slate-100 text-slate-600",
    green: "bg-green-100 text-green-700",
    red: "bg-red-100 text-red-700",
    blue: "bg-blue-100 text-blue-700",
    amber: "bg-amber-100 text-amber-700",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <Card className="p-10 text-center">
      <p className="text-sm font-medium text-slate-700">{title}</p>
      {hint && <p className="mt-1 text-sm text-slate-400">{hint}</p>}
    </Card>
  );
}

export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
    </label>
  );
}

export const inputClass =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100";
